import { useTheme } from '@mui/material';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import Map from 'react-map-gl/mapbox';
import { useSelector } from 'react-redux';
import { selectors as appSelectors } from 'redux/app/app.slice';
import { selectRequestedStoresWithOrderingDisabled } from 'redux/locations/locations.selectors';
import { getEnv } from 'util/getEnv';
import { getLatLngFromStores } from '../locations.utils';
import { getMapBoxBounds } from './locations-map-mapbox.utils';
import UserMarker from './marker/user-marker.component';
import LocationMapMarker from './marker/location-map-marker.component';

const LocationsMapMapbox = ({ setSelectedStore, selectedStore }) => {
  const theme = useTheme();
  const mapboxRef = useRef();
  const stores = useSelector(selectRequestedStoresWithOrderingDisabled);
  const locationFetchingLatLon = useSelector(
    appSelectors.selectLocationFetchingLatLon,
  );
  const {
    latitude: requestedAddressLatitude,
    longitude: requestedAddressLongitude,
  } = locationFetchingLatLon;

  const mapBoxBounds = useMemo(
    () => getMapBoxBounds(getLatLngFromStores(stores)),
    [stores],
  );

  const setMapBoundsOrCenter = useCallback(
    map => {
      const mapboxMap = map || mapboxRef.current;

      if (!mapboxMap) return;

      if (requestedAddressLatitude) {
        mapboxMap.easeTo({
          center: [requestedAddressLongitude, requestedAddressLatitude],
          zoom: 12,
          duration: 1500,
        });
      } else {
        mapboxMap.fitBounds(mapBoxBounds, { padding: 50 });
      }
    },
    [mapBoxBounds, requestedAddressLatitude, requestedAddressLongitude],
  );

  useEffect(() => {
    setMapBoundsOrCenter();
  }, [setMapBoundsOrCenter]);

  return (
    <Map
      mapboxAccessToken={getEnv('REACT_APP_INC_MAPBOX_ACCESS_TOKEN')}
      initialViewState={{
        zoom: 4,
        bounds: mapBoxBounds,
      }}
      mapStyle={
        theme.palette.mode === 'dark'
          ? 'mapbox://styles/mapbox/dark-v11'
          : 'mapbox://styles/mapbox/streets-v12'
      }
      ref={mapboxRef}
      onLoad={e => {
        /* istanbul ignore next */
        setMapBoundsOrCenter(e.target);
      }}
      testMode={!!window.Cypress}
    >
      {!!requestedAddressLatitude && (
        <UserMarker
          latitude={requestedAddressLatitude}
          longitude={requestedAddressLongitude}
          anchor="bottom"
        />
      )}

      {stores.map(store => (
        <LocationMapMarker
          key={`${store.storeId}marker`}
          store={store}
          setSelectedStore={setSelectedStore}
          selectedStore={selectedStore}
          latitude={store.latitude}
          longitude={store.longitude}
          anchor="bottom"
        />
      ))}
    </Map>
  );
};
export default LocationsMapMapbox;
